import { Suspense, useMemo, useState } from 'react';
import { trackCreativeView, trackEvent } from '@kcd/event-tracker';

import LoadingSpinnerCenter from '~/components/LoadingSpinnerCenter';
import {
  useCouponDownloadAllMutation,
  useDownloadedCouponsQuery,
} from '~/domains/coupons/queries';
import { checkIsCouponDownloadable } from '~/domains/coupons/utils';
import { useEffectOnce } from '~/hooks/lifecycle';
import { getOAIErrorMessage } from '~/libs/openapi';
import { OAIApiError } from '~/libs/openApiError';
import type { OAISchema } from '~/types/openapi';
import { appAlert, openDialogAsync } from '~/utils/mobileApp';

import { EventPlacementList } from './EventPlacementList';
import { MainImage } from './MainImage';
import EventFloatingWrapper from '../../components/EventFloatingWrapper';
import Terms from '../../components/Terms';

const EventContainer = ({
  data,
}: {
  data: OAISchema['PlacementEventResponse'];
}) => {
  const [disabledCoupon, setDisabledCoupon] = useState(false);

  const downloadAllCoupon = useCouponDownloadAllMutation();

  const {
    adGroupId,
    creativeId,
    items,
    mainImage,
    couponIds = [], // 쿠폰이 없는경우도 존재
    ctaText,
    termsImage,
  } = data;

  const { data: downloadedCoupons = [] } = useDownloadedCouponsQuery(couponIds);

  // 다운로드 가능한 쿠폰이 하나라도 있는지 체크
  const hasDownloadableCoupon = downloadedCoupons.some(
    checkIsCouponDownloadable,
  );

  const onClickCouponDownload = async (eventName: string) => {
    if (!hasDownloadableCoupon || disabledCoupon || !couponIds.length) return;

    try {
      // 쿠폰 다운로드버튼을 빠르게 여러번 누르는걸 방지하기위함
      setDisabledCoupon(true);

      await downloadAllCoupon({
        requestBody: {
          couponIds,
        },
      });

      trackEvent(eventName, {
        couponName: couponIds,
      });

      void openDialogAsync({
        type: 'alert',
        title: '쿠폰이 발급되었습니다.',
        description: '[마이페이지 > 할인쿠폰] 에서 발급된 쿠폰을 확인하세요.',
      });
    } catch (err) {
      if (err instanceof OAIApiError) {
        appAlert({
          title: '쿠폰 발급에 실패했습니다.',
          description: getOAIErrorMessage(err),
        });

        setDisabledCoupon(false);
      }

      throw err;
    }
  };

  useEffectOnce(() => {
    trackCreativeView({
      adGroupId,
      creativeId,
      placementName: data.name || '',
    });
  });

  const disabled = useMemo(() => {
    return disabledCoupon || !hasDownloadableCoupon;
  }, [disabledCoupon, hasDownloadableCoupon]);

  return (
    <>
      {mainImage && mainImage?.default && (
        <button
          type="button"
          onClick={() =>
            onClickCouponDownload('downloaded coupon in 마켓/이벤트/최상단')
          }
        >
          <MainImage
            imageUrl={
              disabled
                ? mainImage?.disabled || mainImage.default
                : mainImage.default
            }
          />
        </button>
      )}
      <Suspense
        fallback={
          <div className="h-[400px]">
            <LoadingSpinnerCenter />
          </div>
        }
      >
        <EventPlacementList items={items} />
      </Suspense>

      {termsImage && <Terms termsImage={termsImage} />}

      <EventFloatingWrapper
        ctaText={ctaText}
        disabled={disabled}
        onClickCouponDownload={onClickCouponDownload}
      />
    </>
  );
};

export default EventContainer;
